import { useEffect, useState, type RefObject } from 'react';
import { MdArrowUpward } from 'react-icons/md';
import clsx from 'clsx';

/**
 * Botão de voltar ao topo do painel central.
 *
 * **Escuta o elemento, não a janela.** O shell do backoffice é `h-screen
 * overflow-hidden` e quem rola é o `<main>` (ver `BackofficeLayout`), então
 * `window.scrollY` é sempre 0 e um listener de `window` nunca dispararia. Por
 * isso o componente recebe a ref do container de rolagem em vez de descobrir
 * sozinho.
 *
 * Fica sempre montado e alterna por opacidade, para aparecer e sumir sem salto.
 * Enquanto escondido sai do fluxo de interação (`pointer-events-none`,
 * `tabIndex={-1}`, `aria-hidden`) — um botão invisível que ainda recebe Tab é
 * uma armadilha para quem navega por teclado.
 */

/**
 * Distância de rolagem, em pixels, a partir da qual o botão aparece.
 *
 * Alto o bastante para não piscar em rolagem curta, e menor que uma tela cheia
 * para já estar disponível quando o topo saiu de vista.
 */
export const DISTANCIA_PARA_APARECER = 320;

export default function BackToTop({ scrollRef }: Readonly<{ scrollRef: RefObject<HTMLElement | null> }>) {
  const [visivel, setVisivel] = useState(false);

  useEffect(() => {
    const alvo = scrollRef.current;
    if (!alvo) return;

    // Passar o mesmo booleano não re-renderiza (React descarta o update), então
    // o listener pode rodar a cada evento de rolagem sem throttle próprio.
    const aoRolar = () => setVisivel(alvo.scrollTop > DISTANCIA_PARA_APARECER);

    aoRolar();
    alvo.addEventListener('scroll', aoRolar, { passive: true });
    return () => alvo.removeEventListener('scroll', aoRolar);
  }, [scrollRef]);

  function voltarAoTopo() {
    const alvo = scrollRef.current;
    if (!alvo) return;
    // Rolagem suave é movimento: quem pediu redução do sistema recebe o salto direto.
    const reduzirMovimento = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    alvo.scrollTo({ top: 0, behavior: reduzirMovimento ? 'auto' : 'smooth' });
  }

  return (
    <button
      type="button"
      data-testid="back-to-top"
      onClick={voltarAoTopo}
      aria-label="Voltar ao topo"
      aria-hidden={!visivel}
      tabIndex={visivel ? 0 : -1}
      className={clsx(
        // `rounded-lg`, e não um círculo: a escala do design system reserva
        // `rounded-full` para pill/badge, e um flutuante de controle é `rounded-lg`.
        'absolute bottom-6 right-6 z-20 rounded-lg bg-brand-600 p-3 text-ink-on-brand shadow-lg',
        // Propriedades nomeadas, e não `transition-all`: este botão vive por
        // cima do painel de rolagem, e `all` põe na transição qualquer
        // propriedade que venha a mudar — inclusive as de layout, que custam
        // um reflow por frame. Aqui só mudam estas três, e nenhuma custa.
        'transition-[opacity,transform,background-color] duration-200 ease-out motion-reduce:transition-none',
        'hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        visivel ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-2 opacity-0',
      )}
    >
      <MdArrowUpward className="text-xl" aria-hidden="true" />
    </button>
  );
}
